import { WORKFLOW_AGENT_STEP_ATTACHMENT_ACCEPT } from '@commenda/api';
import {
  Button,
  IconButton,
  Icons,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  TruncatedText,
} from '@commenda/kakashi';
import { TSysAgentStepAttachment } from '@commenda/types';
import { useRef } from 'react';

import { useAgentStepAttachmentUpload } from './useAgentStepAttachmentUpload';

interface AgentStepAttachmentsFieldProps {
  attachments: TSysAgentStepAttachment[];
  onChange: (attachments: TSysAgentStepAttachment[] | undefined) => void;
}

export const AgentStepAttachmentsField = ({
  attachments,
  onChange,
}: AgentStepAttachmentsFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    pendingFileName,
    isSlowExtraction,
    uploadError,
    lastExtractedAttachment,
    onFileSelected,
  } = useAgentStepAttachmentUpload({
    onAttached: (attachment) =>
      onChange([
        ...attachments,
        { fileId: attachment.fileId, fileName: attachment.fileName },
      ]),
  });

  const onAttachmentRemove = (fileId: number) => {
    const next = attachments.filter(
      (attachment) => attachment.fileId !== fileId
    );
    // Empty must become undefined: the canvas's dirty check is a JSON string compare, so an empty
    // array where the field was previously absent would leave it dirty forever.
    onChange(next.length ? next : undefined);
  };

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center justify-between gap-md">
        <Label optional>Files</Label>
        <Button
          variant="tertiary"
          size="sm"
          disabled={Boolean(pendingFileName)}
          onClick={() => inputRef.current?.click()}
        >
          <Icons.Plus className="size-4" />
          Attach a file
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={WORKFLOW_AGENT_STEP_ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const picked = event.target.files?.[0];
          // Cleared unconditionally so picking the same file again still fires onChange.
          event.target.value = '';
          if (picked) void onFileSelected(picked);
        }}
      />

      {attachments.length > 0 ? (
        <div className="flex flex-col gap-xs">
          {attachments.map((attachment) => (
            <div
              key={attachment.fileId}
              className="flex items-center justify-between gap-sm rounded-lg border border-border-primary px-md py-sm"
            >
              <div className="flex min-w-0 items-center gap-sm">
                <Icons.File06 className="size-4 shrink-0 text-fg-tertiary" />
                <TruncatedText className="txt-extra-small-regular text-text-primary">
                  {attachment.fileName}
                </TruncatedText>
              </div>
              <IconButton
                variant="destructive"
                size="sm"
                aria-label={`Remove ${attachment.fileName}`}
                icon={<Icons.Trash01 className="size-4" />}
                onClick={() => onAttachmentRemove(attachment.fileId)}
              />
            </div>
          ))}
        </div>
      ) : null}

      {pendingFileName ? (
        <div className="flex flex-col gap-xxs">
          <div className="flex items-center gap-sm">
            <LoadingIndicator size={16} />
            <span className="txt-extra-small-regular text-text-secondary">
              Reading “{pendingFileName}”…
            </span>
          </div>
          {isSlowExtraction ? (
            <span className="txt-extra-small-regular text-text-tertiary">
              A PDF is read page by page and can take up to a minute. Keep this
              panel open.
            </span>
          ) : null}
        </div>
      ) : null}

      {uploadError ? <InputErrorMessage message={uploadError} /> : null}

      {lastExtractedAttachment ? (
        <span className="txt-extra-small-regular text-text-tertiary">
          Read{' '}
          {lastExtractedAttachment.extractedCharacterCount.toLocaleString()}{' '}
          characters from “{lastExtractedAttachment.fileName}”.
          {lastExtractedAttachment.truncated
            ? ' Only the first 100,000 characters are handed to the agent.'
            : ''}
        </span>
      ) : null}
    </div>
  );
};
